import classNames from 'classnames'
import React, { Fragment, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CampaignResultsStatusBadge } from '@pages/Traders-Room/Campaign-Results/components/CampaignResultsStatusBadge'

import { AuthSessionContext } from '@context/AuthContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { Body2 } from '@ui/Typography'
import { Link } from '@ui/components/Link'

import { formatDate } from '@utils/date.utils'
import { wait } from '@utils/wait'

import { ClientRebateCampaignDto } from '@model/CampaignResultDto'

import { ScrollToIds, useScrollToElementIds } from '@hooks/useScrollToElementIds'

import styles from './CampaignResultCard.module.scss'

interface CampaignResultCardProps {
  campaignResult: ClientRebateCampaignDto
  cardId?: string

  setSelectedCampaign(campaign: ClientRebateCampaignDto): void
}

export const CampaignResultCard: React.FC<CampaignResultCardProps> = (props) => {
  const { campaignResult, setSelectedCampaign, cardId } = props

  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [auth] = useContext(AuthSessionContext)
  const { scrollIntoView } = useScrollToElementIds()
  const isArabic = useArabicSessionLanguage()
  const dateFormat = auth?.dateFormatType?.name

  return (
    <div
      onClick={() => {
        if (!expanded && cardId) {
          scrollIntoView([cardId])
        }
        setExpanded((prevState) => !prevState)
      }}
      className={styles.card}
      id={cardId}
    >
      <div className={styles.title}>
        <Body2 className={styles.text} dir={isArabic ? 'rtl' : ''} medium>
          {campaignResult.name}
        </Body2>
        <CampaignResultsStatusBadge {...campaignResult} />
      </div>
      {expanded && (
        <Fragment>
          <div className={styles.subtitle}>
            <span className={styles.secondary}>{t('Campaigns.Start Date')}</span>
            <span className={styles.primary}>
              {formatDate(campaignResult.from, { formatType: dateFormat, withTime: false })}
            </span>
          </div>
          <div className={styles.subtitle}>
            <span className={styles.secondary}>{t('Campaigns.End Date')}</span>
            <span className={styles.primary}>
              {formatDate(campaignResult.to, { formatType: dateFormat, withTime: false })}
            </span>
          </div>
          <div className={styles.subtitle}>
            <span className={styles.secondary}>{t('Campaigns.Currency')}</span>
            <span className={styles.primary}>{campaignResult.currency.id}</span>
          </div>
          <div className={styles.subtitle}>
            <span className={styles.secondary}>{t('Campaigns.Campaign Account No')}</span>
            <span
              className={classNames({
                [styles.strikeThrough]: campaignResult.disqualified,
                [styles.primary]: !campaignResult.disqualified,
              })}
            >
              {campaignResult.account && `${campaignResult.account}`}
            </span>
          </div>

          {!campaignResult.internalOnly && (
            <div className={styles.more}>
              <Link
                onClick={() => {
                  scrollIntoView([ScrollToIds.CampaignResultsHeader])
                  wait(500).then(() => setSelectedCampaign(campaignResult))
                }}
              >
                {t('Campaigns.View results')}
              </Link>
            </div>
          )}
        </Fragment>
      )}
    </div>
  )
}
